(function () {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const instances = new Map();

  function buildStops(types) {
    const stops = [];
    for (let index = 0; index < types.length; index += 1) {
      const type = types[index];
      const next = types[index + 1];
      if (type === 'full') {
        stops.push({ left: index, mode: 'single' });
        if (next === 'half') stops.push({ left: index, mode: 'pair' });
      } else if (next === 'half') {
        stops.push({ left: index, mode: 'pair' });
      } else if (index === 0) {
        stops.push({ left: index, mode: 'single-half' });
      }
    }
    return stops;
  }

  function layoutSlides(types, stop, viewport) {
    const full = Math.max(0, Math.round(viewport));
    const half = Math.floor(full / 2);
    let x = 0;
    const boxes = types.map((type, index) => {
      let width;
      if (type === 'half') width = half;
      else if (stop.mode === 'single' && index === stop.left) width = full;
      else if (stop.mode === 'pair' && index === stop.left) width = half;
      else if (index < stop.left) width = half;
      else width = full;

      if (stop.mode === 'pair' && index === stop.left + 1 && type === 'half') {
        width = full - half;
      }

      let gap = 0;
      if (stop.mode === 'single-half' && index === stop.left) {
        gap = Math.max(0, full - width);
      }

      const box = { index: index, width: width, gap: gap, x: x };
      x += width + gap;
      return box;
    });
    const translate = boxes[stop.left] ? boxes[stop.left].x : 0;
    return { boxes: boxes, translate: translate };
  }

  function stopIndexForSlide(stops, slideIndex) {
    const asLeft = stops.findIndex(function (stop) {
      return stop.left === slideIndex;
    });
    if (asLeft !== -1) return asLeft;
    const asRight = stops.findIndex(function (stop) {
      return stop.mode === 'pair' && stop.left + 1 === slideIndex;
    });
    if (asRight !== -1) return asRight;
    return 0;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseSources(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function pickSources(video, mobile) {
    const desktop = parseSources(video.getAttribute('data-sources-desktop'));
    const phone = parseSources(video.getAttribute('data-sources-mobile'));
    if (mobile) return phone.length ? phone : desktop;
    return desktop.length ? desktop : phone;
  }

  function playVideo(video) {
    if (!video || video.classList.contains('is-failed')) return;
    video.muted = true;
    const pending = video.play();
    if (pending && typeof pending.catch === 'function') pending.catch(function () {});
  }

  function pauseVideo(video) {
    if (!video || video.paused) return;
    video.pause();
  }

  function ensureVideo(video, mobile) {
    const mode = mobile ? 'mobile' : 'desktop';
    if (video.getAttribute('data-loaded') === mode) return;
    const sources = pickSources(video, mobile).filter(function (source) {
      return source && source.url;
    });
    if (!sources.length) {
      video.classList.add('is-failed');
      return;
    }
    video.classList.remove('is-failed');
    while (video.firstChild) video.removeChild(video.firstChild);
    sources.forEach(function (source) {
      const element = document.createElement('source');
      element.src = source.url;
      if (source.type) element.type = source.type;
      video.appendChild(element);
    });
    const poster = mobile
      ? video.getAttribute('data-poster-mobile') || video.getAttribute('data-poster-desktop')
      : video.getAttribute('data-poster-desktop') || video.getAttribute('data-poster-mobile');
    if (poster) video.setAttribute('poster', poster);
    video.setAttribute('data-loaded', mode);
    video.load();
  }

  function translateX(element) {
    const value = getComputedStyle(element).transform;
    if (!value || value === 'none') return 0;
    return new DOMMatrix(value).m41;
  }

  function scrollThumbIntoView(rail, thumb) {
    if (!rail || !thumb) return;
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < railRect.left) {
      rail.scrollLeft -= railRect.left - thumbRect.left;
    } else if (thumbRect.right > railRect.right) {
      rail.scrollLeft += thumbRect.right - railRect.right;
    }
  }

  const root = typeof window !== 'undefined' ? window : globalThis;
  root.GalleryBanner = {
    buildStops: buildStops,
    layoutSlides: layoutSlides,
    stopIndexForSlide: stopIndexForSlide,
  };

  if (typeof document === 'undefined') return;

  function init(section) {
    if (!section || instances.has(section)) return;
    const viewport = section.querySelector('[data-gallery-viewport]');
    const track = section.querySelector('[data-gallery-track]');
    if (!viewport || !track) return;

    const slides = Array.prototype.slice.call(section.querySelectorAll('[data-gallery-slide]'));
    const types = slides.map(function (slide) {
      return slide.getAttribute('data-slide-width') === 'half' ? 'half' : 'full';
    });
    const stops = buildStops(types);
    const thumbs = Array.prototype.slice.call(section.querySelectorAll('[data-gallery-thumb]'));
    const prev = section.querySelector('[data-gallery-prev]');
    const next = section.querySelector('[data-gallery-next]');
    const thumbRail = section.querySelector('[data-gallery-thumbs]');
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);
    const abort = new AbortController();
    const signal = abort.signal;

    const instance = {
      stopIndex: 0,
      lastWidth: -1,
      drag: null,
      watchToken: 0,
      frame: 0,
      observer: null,
      wrapping: false,
      wrapToken: 0,
      wrapTimer: null,
      pendingIndex: 0,
    };
    instances.set(section, instance);

    function isDesktop() {
      return desktopQuery.matches;
    }

    function duration() {
      if (prefersReducedMotion()) return 0;
      const value = parseInt(section.getAttribute('data-duration'), 10);
      return Number.isFinite(value) ? value : 600;
    }

    function setDisabled(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.classList.toggle('is-disabled', disabled);
    }

    function visibleSlideIndexes(stop) {
      if (!stop) return [];
      if (stop.mode === 'pair') return [stop.left, stop.left + 1];
      return [stop.left];
    }

    function updateControls() {
      const stop = stops[instance.stopIndex];
      const locked = stops.length < 2;
      section.classList.toggle('is-locked', locked);
      setDisabled(prev, locked);
      setDisabled(next, locked);
      const activeIndexes = visibleSlideIndexes(stop);
      thumbs.forEach(function (thumb) {
        const index = parseInt(thumb.getAttribute('data-index'), 10);
        const active = activeIndexes.indexOf(index) !== -1;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          scrollThumbIntoView(thumbRail, thumb);
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      section.setAttribute('data-gallery-stop', String(instance.stopIndex));
      if (stop) section.setAttribute('data-gallery-mode', stop.mode);
    }

    function videosIn(slide) {
      return Array.prototype.slice.call(slide.querySelectorAll('[data-gallery-video]'));
    }

    function activeVideo(slide, mobile) {
      const preferred = mobile ? 'mobile' : 'desktop';
      return slide.querySelector('[data-gallery-video="' + preferred + '"]') || slide.querySelector('[data-gallery-video]');
    }

    function syncSlideVideo(slide, mobile, shouldPlay) {
      const video = activeVideo(slide, mobile);
      videosIn(slide).forEach(function (item) {
        if (item !== video) pauseVideo(item);
      });
      if (!video) return;
      if (shouldPlay) {
        ensureVideo(video, mobile);
        playVideo(video);
      } else {
        pauseVideo(video);
      }
    }

    function updateVisibility() {
      if (!isDesktop()) {
        slides.forEach(function (slide, index) {
          slide.classList.toggle('is-visible', index === 0);
          slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
          syncSlideVideo(slide, true, index === 0);
        });
        return;
      }

      const view = viewport.getBoundingClientRect();
      slides.forEach(function (slide) {
        const rect = slide.getBoundingClientRect();
        const overlap = Math.min(rect.right, view.right) - Math.max(rect.left, view.left);
        const visible = overlap > 1;
        slide.classList.toggle('is-visible', visible);
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        syncSlideVideo(slide, false, visible);
      });
    }

    function watchVisibility(ms) {
      const token = ++instance.watchToken;
      updateVisibility();
      if (ms <= 0) return;
      const finish = performance.now() + ms + 40;
      const maxFrames = Math.ceil(ms / 16) + 4;
      let frames = 0;
      const stepFrame = function () {
        if (token !== instance.watchToken) return;
        updateVisibility();
        frames += 1;
        if (frames < maxFrames && performance.now() < finish) requestAnimationFrame(stepFrame);
      };
      requestAnimationFrame(stepFrame);
    }

    function clearLayout() {
      clearClones();
      track.style.transition = 'none';
      track.style.transform = 'none';
      slides.forEach(function (slide) {
        slide.style.transition = 'none';
        slide.style.width = '';
        slide.style.flexBasis = '';
        slide.style.marginRight = '';
      });
    }

    function freeze() {
      const matrix = getComputedStyle(track).transform;
      track.style.transition = 'none';
      track.style.transform = !matrix || matrix === 'none' ? 'translate3d(0px,0px,0px)' : matrix;
      slides.forEach(function (slide) {
        const width = Math.round(slide.getBoundingClientRect().width);
        slide.style.transition = 'none';
        slide.style.width = width + 'px';
        slide.style.flexBasis = width + 'px';
        slide.style.marginRight = getComputedStyle(slide).marginRight;
      });
    }

    function write(ms) {
      if (!isDesktop() || !stops.length) return;
      const width = viewport.clientWidth;
      if (width < 1) return;
      const stop = stops[instance.stopIndex];
      const layout = layoutSlides(types, stop, width);
      if (ms > 0) {
        freeze();
        void track.offsetWidth;
      } else {
        track.style.transition = 'none';
        slides.forEach(function (slide) {
          slide.style.transition = 'none';
        });
        void track.offsetWidth;
      }
      const timing = ms > 0 ? ms + 'ms ease' : '0ms linear';
      track.style.transition = 'transform ' + timing;
      slides.forEach(function (slide, index) {
        const box = layout.boxes[index];
        slide.style.transition = ms > 0 ? 'width ' + timing + ', margin-right ' + timing + ', flex-basis ' + timing : 'none';
        slide.style.width = box.width + 'px';
        slide.style.flexBasis = box.width + 'px';
        slide.style.marginRight = box.gap + 'px';
      });
      track.style.transform = 'translate3d(' + -layout.translate + 'px,0,0)';
      instance.lastWidth = width;
      updateControls();
      watchVisibility(ms);
    }

    function clearClones() {
      track.querySelectorAll('[data-gallery-clone]').forEach(function (clone) {
        clone.remove();
      });
    }

    function settleWrap() {
      if (!instance.wrapping) return;
      instance.wrapToken += 1;
      clearTimeout(instance.wrapTimer);
      clearClones();
      instance.wrapping = false;
      instance.stopIndex = instance.pendingIndex;
      write(0);
    }

    function placeIncoming(stop, viewLeft) {
      const width = viewport.clientWidth;
      const layout = layoutSlides(types, stop, width);
      visibleSlideIndexes(stop).forEach(function (index) {
        const source = slides[index];
        const box = layout.boxes[index];
        if (!source || !box) return;
        const clone = source.cloneNode(true);
        clone.removeAttribute('data-gallery-slide');
        clone.setAttribute('data-gallery-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.style.position = 'absolute';
        clone.style.top = '0';
        clone.style.left = (viewLeft + box.x - layout.translate) + 'px';
        clone.style.height = '100%';
        clone.style.width = box.width + 'px';
        clone.style.flexBasis = 'auto';
        clone.style.marginRight = '0';
        clone.style.transition = 'none';
        clone.style.pointerEvents = 'none';
        clone.querySelectorAll('.is-playing').forEach(function (node) {
          node.classList.remove('is-playing');
        });
        clone.querySelectorAll('video').forEach(function (video) {
          video.removeAttribute('data-gallery-video');
          video.pause();
          while (video.firstChild) video.removeChild(video.firstChild);
          video.removeAttribute('src');
        });
        track.appendChild(clone);
      });
    }

    function wrapTo(index, direction) {
      const width = viewport.clientWidth;
      const ms = duration();
      if (width < 1 || ms <= 0) {
        goTo(index, false);
        return;
      }
      clearClones();
      const visual = -translateX(track);
      const distance = direction * width;
      placeIncoming(stops[index], visual + distance);
      instance.wrapping = true;
      instance.pendingIndex = index;
      instance.wrapToken += 1;
      const token = instance.wrapToken;
      instance.stopIndex = index;
      updateControls();
      track.style.transition = 'none';
      void track.offsetWidth;
      track.style.transition = 'transform ' + ms + 'ms ease';
      track.style.transform = 'translate3d(' + (-(visual + distance)) + 'px,0,0)';

      const finish = function () {
        if (token !== instance.wrapToken) return;
        instance.wrapToken += 1;
        clearTimeout(instance.wrapTimer);
        track.removeEventListener('transitionend', onEnd);
        clearClones();
        instance.wrapping = false;
        instance.stopIndex = index;
        write(0);
      };
      const onEnd = function (event) {
        if (event.propertyName !== 'transform') return;
        finish();
      };
      track.addEventListener('transitionend', onEnd);
      instance.wrapTimer = setTimeout(finish, ms + 50);
    }

    function goTo(index, animate) {
      if (!stops.length || !isDesktop()) return;
      if (instance.wrapping) settleWrap();
      instance.stopIndex = Math.max(0, Math.min(stops.length - 1, index));
      write(animate === false ? 0 : duration());
    }

    function step(delta) {
      if (!isDesktop() || stops.length < 2) return;
      if (instance.wrapping) settleWrap();
      const count = stops.length;
      const raw = instance.stopIndex + delta;
      const wrapped = raw < 0 || raw >= count;
      const target = ((raw % count) + count) % count;
      if (wrapped) wrapTo(target, delta > 0 ? 1 : -1);
      else goTo(target, true);
    }

    function applyMode() {
      if (instance.wrapping) settleWrap();
      if (isDesktop()) {
        if (viewport.clientWidth < 1) return;
        write(0);
      } else {
        clearLayout();
        section.classList.remove('is-dragging');
        updateVisibility();
      }
    }

    function onResize() {
      if (!isDesktop()) return;
      const width = viewport.clientWidth;
      if (width < 1 || width === instance.lastWidth) return;
      if (instance.wrapping) {
        instance.wrapToken += 1;
        clearTimeout(instance.wrapTimer);
        clearClones();
        instance.wrapping = false;
        instance.stopIndex = instance.pendingIndex;
      }
      write(0);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        step(-1);
      }, { signal: signal });
    }
    if (next) {
      next.addEventListener('click', function () {
        step(1);
      }, { signal: signal });
    }
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const index = parseInt(thumb.getAttribute('data-index'), 10);
        if (!Number.isFinite(index)) return;
        goTo(stopIndexForSlide(stops, index));
      }, { signal: signal });
    });

    viewport.addEventListener('pointerdown', function (event) {
      if (!isDesktop() || stops.length < 2 || event.button !== 0) return;
      if (instance.wrapping) settleWrap();
      instance.drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        active: false,
      };
    }, { signal: signal });

    viewport.addEventListener('pointermove', function (event) {
      const drag = instance.drag;
      if (!drag || event.pointerId !== drag.id || !isDesktop()) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          instance.drag = null;
          return;
        }
        drag.active = true;
        drag.dx = dx;
        section.classList.add('is-dragging');
        if (viewport.setPointerCapture) {
          try {
            viewport.setPointerCapture(event.pointerId);
          } catch (error) {}
        }
        freeze();
        drag.base = translateX(track);
      }
      drag.dx = dx;
      track.style.transition = 'none';
      track.style.transform = 'translate3d(' + (drag.base + dx) + 'px,0,0)';
      if (event.cancelable) event.preventDefault();
    }, { signal: signal });

    function endDrag(event) {
      const drag = instance.drag;
      if (!drag || event.pointerId !== drag.id) return;
      instance.drag = null;
      section.classList.remove('is-dragging');
      if (!drag.active) return;
      const dx = Number.isFinite(drag.dx) ? drag.dx : event.clientX - drag.x;
      const threshold = Math.max(40, viewport.clientWidth * 0.15);
      if (dx <= -threshold) step(1);
      else if (dx >= threshold) step(-1);
      else goTo(instance.stopIndex);
    }

    viewport.addEventListener('pointerup', endDrag, { signal: signal });
    viewport.addEventListener('pointercancel', endDrag, { signal: signal });

    slides.forEach(function (slide) {
      videosIn(slide).forEach(function (video) {
        const media = video.closest('.gallery-banner__media');
        video.addEventListener('playing', function () {
          if (media) media.classList.add('is-playing');
        }, { signal: signal });
        video.addEventListener('error', function () {
          if (video.getAttribute('data-loaded')) video.classList.add('is-failed');
          if (media) media.classList.remove('is-playing');
        }, { signal: signal });
      });
    });

    document.addEventListener('shopify:block:select', function (event) {
      const target = event.target;
      if (!target || !section.contains(target) || !isDesktop()) return;
      const slide = target.closest('[data-gallery-slide]');
      if (!slide) return;
      const index = parseInt(slide.getAttribute('data-slide-index'), 10);
      if (!Number.isFinite(index)) return;
      goTo(stopIndexForSlide(stops, index), false);
    }, { signal: signal });

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', applyMode);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(applyMode);
    }
    instance.onMedia = applyMode;

    if (typeof ResizeObserver === 'function') {
      instance.observer = new ResizeObserver(onResize);
      instance.observer.observe(viewport);
    } else {
      window.addEventListener('resize', onResize, { signal: signal });
    }

    applyMode();

    instance.destroy = function () {
      instance.wrapToken += 1;
      clearTimeout(instance.wrapTimer);
      clearClones();
      abort.abort();
      if (instance.observer) instance.observer.disconnect();
      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener('change', instance.onMedia);
      } else if (desktopQuery.removeListener) {
        desktopQuery.removeListener(instance.onMedia);
      }
      slides.forEach(function (slide) {
        videosIn(slide).forEach(pauseVideo);
      });
      instances.delete(section);
    };
  }

  function destroy(section) {
    const instance = instances.get(section);
    if (instance && instance.destroy) instance.destroy();
  }

  function initAll(rootNode) {
    const scope = rootNode || document;
    if (scope.matches && scope.matches('[data-gallery-banner]')) init(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[data-gallery-banner]').forEach(init);
  }

  function destroyAll(rootNode) {
    const scope = rootNode || document;
    if (scope.matches && scope.matches('[data-gallery-banner]')) destroy(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('[data-gallery-banner]').forEach(destroy);
  }

  initAll(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });
})();
